import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Modal, Divider } from 'antd';
import SelectEdit from '../../components/SelectEdit';

import selector from './gatesPressingEditor.selector';
import { setSelected } from '../Construction/construction.reducer';

import { availableType } from '../../types/available.types';
import { infoShape } from '../../types/info.types';

import {
  ModalContainer,
  StyledImg,
  ImgWrapper
} from './gatesPressingEditor.styles';

const ALL_GATE_TYPES_ORDER = [
  'swing_gate',
  'double_leaf_gate',
  'sectional_gate',
  'roller_gate'
];

/**
 * TU ustaw klucze tłumaczeń dla labeli typów bram.
 * Zmień wartości jeśli w Twoich translations klucze są inne.
 */
const GATE_TYPE_I18N_KEYS = {
  swing_gate: 'gate_type.swing_gate',
  double_leaf_gate: 'gate_type.double_leaf_gate',
  sectional_gate: 'gate_type.sectional_gate',
  roller_gate: 'gate_type.roller_gate'
};

/**
 * Fallbacki (jakby tłumaczenie nie istniało)
 */
const GATE_TYPE_FALLBACK_LABELS = {
  swing_gate: 'Brama uchylna',
  double_leaf_gate: 'Brama dwuskrzydłowa',
  sectional_gate: 'Brama segmentowa',
  roller_gate: 'Brama rolowana'
};

/**
 * Dostępność przetłoczeń per typ bramy.
 * Wpisuj ID z this.props.available (el.id).
 * null => wszystkie
 */
const PRESSING_AVAILABLE_BY_GATE_TYPE = {
  swing_gate: null,
  double_leaf_gate: null,
  sectional_gate: [6],
  roller_gate: null
};

class GatesPressingEditor extends React.Component {
  static propTypes = {
    loaded: PropTypes.bool.isRequired,
    setSelected: PropTypes.func.isRequired,
    available: PropTypes.arrayOf(availableType),
    info: infoShape,

    // z selector
    translations: PropTypes.object,
    gateTypesPresent: PropTypes.arrayOf(PropTypes.string),

    selectedByType: PropTypes.shape({
      swing_gate: PropTypes.number,
      double_leaf_gate: PropTypes.number,
      sectional_gate: PropTypes.number,
      roller_gate: PropTypes.number
    })
  };

  static defaultProps = {
    available: [],
    translations: {},
    gateTypesPresent: [],
    selectedByType: {
      swing_gate: null,
      double_leaf_gate: null,
      sectional_gate: null,
      roller_gate: null
    },
    info: null
  };

  state = {
    modalVisible: false,
    activeGateType: 'swing_gate'
  };

  componentDidUpdate(prevProps) {
    // jeśli zmieniły się dostępne typy bram i aktualny aktywny typ już nie istnieje -> przestaw
    if (prevProps.gateTypesPresent !== this.props.gateTypesPresent) {
      const visible = this.getVisibleGateTypes();
      if (visible.length === 0) {
        if (this.state.modalVisible) this.setState({ modalVisible: false });
        return;
      }

      if (!visible.includes(this.state.activeGateType)) {
        this.setState({
          activeGateType: visible[0],
          modalVisible: false
        });
      }
    }
  }

  getVisibleGateTypes = () => {
    // gateTypesPresent jest już uporządkowane w selectorze,
    // ale dodatkowo zabezpieczmy kolejność:
    const present = new Set(this.props.gateTypesPresent || []);
    return ALL_GATE_TYPES_ORDER.filter((t) => present.has(t));
  };

  // prosty getter po "kropkach": t("a.b.c")
  t = (key, fallback = '') => {
    if (!key) return fallback;
    const { translations } = this.props;
    const value = String(key)
      .split('.')
      .reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), translations);

    if (value === undefined || value === null || value === '') return fallback;
    return value;
  };

  getGateTypeLabel = (gateType) => {
    const key = GATE_TYPE_I18N_KEYS[gateType];
    return this.t(key, GATE_TYPE_FALLBACK_LABELS[gateType] || gateType);
  };

  openModal = (gateType) => {
    this.setState({ modalVisible: true, activeGateType: gateType });
  };

  closeModal = () => {
    this.setState({ modalVisible: false });
  };

  handleInsideChange = (gateType, value) => {
    const current = this.props.selectedByType?.[gateType] ?? null;

    if (value !== current) {
      this.props.setSelected(`gates_pressing_${gateType}`, value);
      this.closeModal();
    }
  };

  getAvailableForGateType = (gateType) => {
    const rule = PRESSING_AVAILABLE_BY_GATE_TYPE[gateType];

    if (rule === null || rule === undefined) return this.props.available;

    if (Array.isArray(rule)) {
      const allowed = new Set(rule);
      return (this.props.available || []).filter((el) => allowed.has(el.id));
    }

    if (typeof rule === 'function') {
      return (this.props.available || []).filter((el) => rule(el));
    }

    return this.props.available;
  };

  renderSelectForType = (gateType) => {
    const selected = this.props.selectedByType?.[gateType] ?? null;
    const optionsForType = this.getAvailableForGateType(gateType);

    return (
      <SelectEdit
        key={`pressing_${gateType}`}
        selected={selected}
        loaded={this.props.loaded}
        onEditClick={() => this.openModal(gateType)}
        title={this.getGateTypeLabel(gateType)}
        options={optionsForType.map((el) => ({
          id: el.id,
          value: `${el.attributes.value}`
        }))}
      />
    );
  };

  render() {
    if (!this.props.loaded) return <SelectEdit loading />;

    const visibleGateTypes = this.getVisibleGateTypes();

    // ✅ jeśli w konfiguracji nie ma żadnej bramy, nie pokazuj nic
    if (visibleGateTypes.length === 0) {
      return null;
    }

    const { info } = this.props;
    const activeGateType = visibleGateTypes.includes(this.state.activeGateType)
      ? this.state.activeGateType
      : visibleGateTypes[0];

    const activeSelected = this.props.selectedByType?.[activeGateType] ?? null;
    const activeOptions = this.getAvailableForGateType(activeGateType);

    return (
      <React.Fragment>
        {/* ✅ renderuj tylko te typy bram, które są w konfiguracji */}
        {visibleGateTypes.map(this.renderSelectForType)}

        <Modal
          title={`${info?.name || 'Przetłoczenie'} - ${this.getGateTypeLabel(activeGateType)}`}
          open={this.state.modalVisible}
          footer={null}
          onCancel={this.closeModal}
        >
          <ModalContainer>
            {activeOptions.map((el) => (
              <div key={`inside__${activeGateType}__${el.id}`}>
                <ImgWrapper $selected={el.id === activeSelected}
                  onClick={() => this.handleInsideChange(activeGateType, el.id)}
                >
                  <StyledImg src={el.attributes.img} />
                </ImgWrapper>
                <p>{el.attributes.value}</p>
              </div>
            ))}
          </ModalContainer>

          <Divider>{info?.info_title}</Divider>
          {info?.info_content}
        </Modal>
      </React.Fragment>
    );
  }
}

const mapDispatchToProps = {
  setSelected
};

export default connect(selector, mapDispatchToProps)(GatesPressingEditor);
